import { Navigate, Outlet } from "react-router-dom";
import { isPlatformUser, orgRoleRank } from "@/features/auth/roles";
import { type AuthUser, useAuthStore } from "@/features/auth/store/auth-store";

/**
 * Gates the Business-Practitioner directory. GA/SA see every BP; a house
 * Team-Leader-or-above sees the BPs assigned to them, and a house Staff member
 * sees it once assigned to at least one BP (the backend scopes the list to the
 * caller's assignments). Everyone else (unassigned house staff, BP-internal
 * users, taxpayers) is bounced to the dashboard. The server independently
 * scopes/authorizes every call, so this is a UX guard only.
 */
function canSeeBpDirectory(user: AuthUser | null): boolean {
  if (!user) return false;
  if (isPlatformUser(user)) return true;
  if (user.firm?.org_type !== "house") return false;
  // Team-Leader+ always; Staff only once they're assigned to a BP.
  return orgRoleRank(user) >= 2 || (user.assigned_bp_count ?? 0) > 0;
}

export function BpDirectoryRoute() {
  const allowed = useAuthStore((s) => canSeeBpDirectory(s.user));
  if (!allowed) return <Navigate to="/dashboard" replace />;
  return <Outlet />;
}
